import {
  Activity,
  AlertTriangle,
  RotateCcw,
  Search,
  ShieldAlert,
} from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { usePoolPhaseRef } from "@/api/local/phases";
import { resetPool } from "@/api/local/pools";
import type { Pool } from "@/api/types";
import { PhaseCountdown } from "@/components/PhaseCountdown";
import { PoolPhaseBadge } from "@/components/PhaseBadge";
import { Section } from "@/components/Section";

interface Props {
  pool: Pool;
}

/**
 * Pool-dashboard phase summary card.
 *
 * Renders:
 *  - `Pool.phase` as a {@link PoolPhaseBadge},
 *  - the phase's operator-facing description from
 *    `GET /reference/phases`,
 *  - a {@link PhaseCountdown} sized by the descriptor's
 *    `countdown_kind` (live deadline / elapsed-only / tick-boundary /
 *    waiting-for-operator),
 *  - the most recent classified failure when the pool is parked in
 *    `retrying` / `needs_attention`,
 *  - a prominent attention banner with sub-classification text and
 *    an inline Reset action when the pool needs human input.
 */
export function PoolPhaseCard({ pool }: Props) {
  const ref = usePoolPhaseRef(pool.phase);
  const queryClient = useQueryClient();
  const resetMutation = useMutation({
    mutationFn: () => resetPool(pool.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["pool", pool.id] });
      void queryClient.invalidateQueries({ queryKey: ["pools"] });
    },
  });

  const isAttention = pool.phase === "needs_attention";
  const isSpecRequired =
    isAttention && pool.attention_severity === "attention_spec";
  const showLastError =
    !!pool.last_error
    && (pool.phase === "retrying" || pool.phase === "needs_attention");

  // The countdown widget picks the right deadline column based on
  // the descriptor's `countdown_kind`. We resolve here so the
  // widget itself stays generic.
  const deadline =
    pool.phase === "retrying"
      ? pool.next_retry_after
      : pool.phase === "needs_attention"
        ? pool.next_probe_after
        : null;

  return (
    <Section title="Reconciler phase" icon={<Activity />}>
      <div className="phase-card">
        <div className="phase-card__row">
          <PoolPhaseBadge phase={pool.phase} pool={pool} hideHelpLink />
        </div>
        {/* The ONE honest story: phase + latest error/rejection folded
            together server-side. This is the headline so the card never
            tells two contradicting stories (a calm "Launching" badge
            next to an error list). The generic phase description
            follows, muted, as background reading. */}
        {pool.status_summary && (
          <p
            className="phase-card__summary"
            data-level={pool.attention_level}
          >
            {pool.status_summary}
          </p>
        )}
        {ref?.description && (
          <p className="muted phase-card__desc">{ref.description}</p>
        )}
        {pool.phase_reason && (
          <p className="phase-card__reason">{pool.phase_reason}</p>
        )}
        {ref && (
          <PhaseCountdown
            kind={
              // attention_spec overrides the descriptor's default
              // (live_seconds) because there is no probe deadline
              // — the pool is fully stopped until the operator
              // edits the spec.
              isSpecRequired ? "operator_wait" : ref.countdown_kind
            }
            deadline={deadline}
            phaseChangedAt={pool.phase_changed_at}
            expectedSeconds={ref.expected_duration_seconds}
            nextActionLabel={ref.next_action_label}
          />
        )}
        {isAttention && (
          <div className="phase-card__banner phase-card__banner--danger">
            <ShieldAlert size={14} />
            <div className="phase-card__banner-body">
              <strong>
                {isSpecRequired
                  ? "Operator action required"
                  : "Stuck — controller is probing in the background"}
              </strong>
              <p>
                {isSpecRequired
                  ? "The reconciler has stopped retrying because the failure requires a configuration change. Edit the workload / pool spec to resume — saving auto-clears this state — or hit Reset to try again as-is."
                  : "The reconciler keeps probing at low frequency so the pool will resume automatically once the external cause clears (image push / quota approval / capacity recovery). You can also force an immediate retry via Reset."}
              </p>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => resetMutation.mutate()}
                disabled={resetMutation.isPending}
              >
                <RotateCcw size={12} />
                {resetMutation.isPending ? "Resetting…" : "Reset pool state"}
              </button>
            </div>
          </div>
        )}
        {pool.current_attempt && (
          <div className="phase-card__banner phase-card__banner--info">
            <Search size={14} />
            <div>
              <strong>Currently attempting</strong>
              <p className="phase-card__attempt">
                {[
                  pool.current_attempt.cloud,
                  pool.current_attempt.region,
                  pool.current_attempt.instance_type,
                ]
                  .filter(Boolean)
                  .join(" / ") || "(SkyPilot launcher starting)"}
                {" "}
                <span className="muted">
                  (loop {pool.attempt_loop_count + 1}, attempt{" "}
                  {pool.current_attempt.attempt_index})
                </span>
              </p>
              {pool.current_attempt.rejected_reason && (
                <p className="muted phase-card__attempt-reason">
                  last rejection:{" "}
                  <code>{pool.current_attempt.rejected_reason}</code>
                </p>
              )}
            </div>
          </div>
        )}
        {showLastError && (
          <div className="phase-card__banner phase-card__banner--warn">
            <AlertTriangle size={14} />
            <div>
              <div className="phase-card__error-kind">
                {pool.last_error_kind ?? "error"}
              </div>
              <pre className="phase-card__error-body">{pool.last_error}</pre>
            </div>
          </div>
        )}
        {pool.consecutive_failure_count > 0
          && pool.phase !== "serving"
          && pool.phase !== "stopped" && (
            <p className="muted phase-card__counter">
              Consecutive failures: {pool.consecutive_failure_count}
            </p>
          )}
      </div>
    </Section>
  );
}
